import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Download, Loader2, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 10/20: READ-ONLY referral splits — per ISO deal, the 2% pool (Σ iso_charge, PFS excluded) × the ISO's referrer's split %.
 * The split % lives on the Referrer contact; until it is typed the row says so and nothing is computed.
 * Payouts recorded above (status Paid) are netted per deal + referrer. Nothing here writes.
 */
export default function ReferralSplits() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyOpen, setOnlyOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payouts/referral-splits`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(`Referral splits: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/payouts/referral-splits/export.csv`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'referral_splits.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const rows = (data?.rows || []).filter((r) => !onlyOpen || r.open == null || r.open > 0.005 || !r.referrer_id);
  const totals = data?.totals_by_referrer || {};

  return (
    <Card data-testid="referral-splits">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Percent className="h-4 w-4 text-emerald-600" /> Referral splits (computed)</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Per ISO deal: the 2% pool (sum of the positions&apos; ISO charge, PFS excluded) × the referrer&apos;s split %. The split % is set on the
              Referrer contact; the ISO&apos;s referrer is set on the ISO contact. Paid = referrer payouts recorded above for that deal.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant={onlyOpen ? 'default' : 'outline'} size="sm" onClick={() => setOnlyOpen((v) => !v)}>{onlyOpen ? 'Open only' : 'All deals'}</Button>
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4 mr-1" /> CSV</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : (
          <>
            {(data?.unpriced > 0 || data?.no_referrer > 0) && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                {data.unpriced > 0 && <>{data.unpriced} deal{data.unpriced === 1 ? '' : 's'} have a referrer but no split % yet — set it on the Referrer contact (<Link to="/contacts" className="underline">Contacts</Link> → Edit → Referral Split). </>}
                {data.no_referrer > 0 && <>{data.no_referrer} ISO deal{data.no_referrer === 1 ? '' : 's'} have no referrer on their ISO contact.</>}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {Object.entries(totals).map(([name, t]) => (
                <div key={name} className="rounded border border-slate-200 p-3">
                  <div className="text-xs text-slate-500">{name}</div>
                  <div className="text-xl font-bold text-emerald-700">{formatCurrency(t.open)} <span className="text-xs font-normal text-slate-500">open</span></div>
                  <div className="text-xs text-slate-500">pool {formatCurrency(t.pool)} · owed {formatCurrency(t.owed)} · paid {formatCurrency(t.paid)} · {t.deals} deal{t.deals === 1 ? '' : 's'}{t.unpriced ? ` · ${t.unpriced} without split %` : ''}</div>
                </div>
              ))}
              {Object.keys(totals).length === 0 && <p className="text-sm text-slate-400">No ISO deal has a referrer on its ISO contact yet.</p>}
            </div>
            {rows.length === 0 ? (
              <p className="text-center text-slate-400 py-6">Nothing {onlyOpen ? 'open' : 'to show'}.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Deal</TableHead><TableHead>Merchant</TableHead><TableHead>ISO</TableHead><TableHead>Referrer</TableHead>
                    <TableHead className="text-right">Split %</TableHead><TableHead className="text-right">2% pool</TableHead>
                    <TableHead className="text-right">Owed</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Open</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.deal_id}>
                      <TableCell className="font-medium"><Link className="text-blue-600 hover:underline" to={`/deals/${r.deal_id}`}>#{r.deal_id_number}</Link></TableCell>
                      <TableCell className="text-slate-600 max-w-[200px] truncate">{r.deal_name}</TableCell>
                      <TableCell className="text-slate-600">{r.iso_name || '—'}</TableCell>
                      <TableCell>{r.referrer_name || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-right">{r.split_pct != null ? `${r.split_pct}%` : <span className="text-amber-700">not set</span>}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.pool)}</TableCell>
                      <TableCell className="text-right font-semibold">{r.owed != null ? formatCurrency(r.owed) : '—'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.paid)}</TableCell>
                      <TableCell className="text-right">{r.open != null ? <span className={r.open > 0.005 ? 'text-amber-700 font-semibold' : 'text-green-700'}>{formatCurrency(r.open)}</span> : '—'}</TableCell>
                      <TableCell>{r.reason ? <Badge variant="secondary">{r.reason}</Badge> : null}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
